/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	Fragment,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { mergeRefs } from 'use-callback-ref';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { PrimaryButton, type PrimaryButtonProps } from '@atlaskit/atlassian-navigation';
import type { ButtonProps } from '@atlaskit/button/button';
import type { IconButtonProps } from '@atlaskit/button/icon/button';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { RovoIcon } from '@atlaskit/logo';
import { TopNavButton } from '@atlaskit/navigation-system/theming/top-nav-button';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TooltipProps } from '@atlaskit/tooltip/types';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { usePostOfficeCoordination } from '@atlassian/conversation-assistant-onboarding/controllers/post-office-migration';
import {
	useConversationAssistantSpotlightActions,
	useConversationAssistantSpotlightState,
} from '@atlassian/conversation-assistant-onboarding/controllers/spotlight';
import { AsyncAtlasHomeFomoSpotlightPlacement } from '@atlassian/conversation-assistant-onboarding/ui/AtlasHomeFomoSpotlightAsync';
import { AsyncRovoFullPageSpotlightPlacement } from '@atlassian/conversation-assistant-onboarding/ui/RovoFullPageSpotlightAsync';
import { ConversationAssistantOnboardingSpotlight } from '@atlassian/conversation-assistant-onboarding/ui/Spotlight';
import { ProfileContext } from '@atlassian/conversation-assistant-store/controllers/profile';
import { usePostOfficePlacement } from '@atlassian/conversation-assistant-utils/usePostOfficePlacement';
import { useInsightsStatus } from '@atlassian/rovo-growth-pulse/services/insights-status';

import { ChunkErrorBoundary } from '../../common/ui/chunk-error-boundary/ChunkErrorBoundary';
import {
	ConditionalWrapper,
	type ConditionalWrapperChildren,
} from '../../common/ui/conditional-wrapper';
import { getDefaultShareBaseUrl } from '../share-conversation-modal/shareConversationUrl';
import { messages } from './messages';
import { prefetchPromptOfTheDay } from './prefetchPromptOfTheDay';

type Props = {
	testId?: string;
	isActive: boolean;
	isDisabled?: boolean;
	onClick: ButtonProps['onClick'];
	onMouseEnter?: React.MouseEventHandler<Element>;
	label?: React.ReactNode;
	tooltipContent?: React.ReactNode;
	shortcut?: TooltipProps['shortcut'];
	orgId?: string;
	siteId: string;
	productKey: string;
	analyticsAttributes?: Record<string, string | number | boolean | undefined>;
	/**
	 * Opt in to the Post Office onboarding spotlight coordination flow.
	 *
	 * When omitted (the default) the button is pure and does not depend on Post Office, so it can be
	 * rendered in surfaces that do not provide a `PostOfficeProvider` (for example the editor's Ask
	 * Rovo floating toolbar). Only navigation surfaces that mount a Post Office provider and want the
	 * agents-onboarding spotlight should set this to `true`.
	 */
	enableOnboardingSpotlight?: boolean;
};

const defaultButtonStyles = {
	paddingInline: token('space.100'),
};

const inactiveButtonStyles = {
	background: token('color.background.neutral'),
};

const disabledButtonStyles = {
	background: token('color.background.disabled'),
	color: token('color.text.disabled'),
};

const conversationAssistantButtonWrapperNoOverlayStyles = css({
	// Override button theme background for both PrimaryButton and Nav4 (TopNavButton), need to disable lint rule for nested styles/selectors because of custom theming possibly colliding
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button': {
		backgroundColor: token('elevation.surface'),
		color: token('color.text'),
		fontWeight: token('font.weight.medium'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button:hover': {
		backgroundColor: token('elevation.surface.hovered'),
		color: token('color.text'),
	},
});

const topNavButtonFlareKeyframes = keyframes({
	'0%': {
		transform: 'translate(-120%, -25%)', // off-screen left, high (entry)
		opacity: 0,
		animationTimingFunction: 'linear',
	},
	'15%': {
		opacity: 1, // fully visible
		transform: 'translate(-70%, -25%)', // top-left corner of button
		animationTimingFunction: 'linear',
	},
	// Parabola-approximated U-curve: equal arc-length sampling between corners
	'25%': {
		transform: 'translate(-50%, -10%)',
		animationTimingFunction: 'linear',
	},
	'33%': {
		transform: 'translate(-32%, 8%)',
		animationTimingFunction: 'linear',
	},
	'42%': {
		transform: 'translate(-15%, 22%)',
		animationTimingFunction: 'linear',
	},
	'50%': {
		transform: 'translate(0%, 25%)', // bottom of the U
		animationTimingFunction: 'linear',
	},
	'58%': {
		transform: 'translate(15%, 22%)',
		animationTimingFunction: 'linear',
	},
	'67%': {
		transform: 'translate(32%, 8%)',
		animationTimingFunction: 'linear',
	},
	'75%': {
		transform: 'translate(50%, -10%)',
		animationTimingFunction: 'linear',
	},
	'85%': {
		opacity: 1,
		transform: 'translate(100%, -25%)', // top-right corner of button
		animationTimingFunction: 'linear',
	},
	'100%': {
		transform: 'translate(120%, -25%)', // off-screen right, high
		opacity: 0,
	},
});

const topNavButtonFlareAnimationStyles = css({
	display: 'inline-flex',
	clipPath: `inset(0 round ${token('radius.small')})`,
	overflow: 'hidden',
	position: 'relative',
	borderRadius: token('radius.small'),

	// Make the inner button transparent so the glow on the wrapper shows through behind the text/logo
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button': {
		backgroundColor: 'transparent',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& button:hover': {
		backgroundColor: 'transparent',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'&::before': {
		filter: 'blur(12px)',
		animationDuration: '3000ms',
		animationIterationCount: 1,
		animationName: topNavButtonFlareKeyframes,
		// The white halo uses rgba(255, 255, 255, ...) intentionally — it's a decorative
		// additive-light effect that needs to work the same way in both light and dark modes.
		// Theme-aware neutral tokens would invert (darken) this halo in dark mode.
		background: `
			radial-gradient(circle 28px at 50% 50%,
				rgba(255, 255, 255, 0.4) 0%,
				transparent 100%),
			radial-gradient(circle 28px at 50% 50%,
				rgba(255, 255, 255, 0.2) 0%,
				rgba(255, 255, 255, 0.1) 50%,
				transparent 100%),
			radial-gradient(circle 22px at 55% 50%,
				${token('color.background.accent.orange.subtler')} 0%,
				transparent 100%),
			radial-gradient(circle 28px at 50% 55%,
				${token('color.background.accent.magenta.subtle')} 0%,
				transparent 100%),
			radial-gradient(circle 28px at 50% 50%,
				${token('color.background.accent.purple.subtle')} 0%,
				transparent 100%),
			radial-gradient(circle 28px at 60% 55%,
				${token('color.background.accent.blue.subtle')} 0%,
				transparent 100%)
		`,
		blockSize: '200%',
		content: '""',
		inlineSize: '100%',
		insetBlockStart: '-50%',
		insetInlineStart: 0,
		opacity: 0,
		pointerEvents: 'none',
		position: 'absolute',
		zIndex: 0,
	},

	'@media (prefers-reduced-motion: reduce)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
		'&::before': {
			animation: 'none',
			opacity: 0,
		},
	},
});

const baseShadow = `0 0 0 2px ${token('color.border.discovery')}`;

const pulseKeyframes = keyframes({
	'0%, 33%': {
		boxShadow: `${baseShadow}, 0 0 0 ${token('color.border.discovery')}`,
	},
	'66%, 100%': {
		boxShadow: `${baseShadow}, 0 0 0 10px rgba(101, 84, 192, 0.01)`,
	},
});

const pulseAnimationStyles = css({
	animationDuration: '3000ms',
	animationIterationCount: 'infinite',
	animationName: pulseKeyframes,
	animationTimingFunction: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)',
	boxShadow: baseShadow,
	borderRadius: token('radius.small'),
});

const reduceMotionStyles = css({
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
		transition: 'none',
	},
});

const getAtlasChatUrl = (siteId: string, orgId?: string): string => {
	const orgPath = orgId ? `/o/${encodeURIComponent(orgId)}` : '';
	return addRovoParamsToUrl(
		`${getDefaultShareBaseUrl()}${orgPath}/chat?cloudId=${encodeURIComponent(siteId)}`,
		{
			cloudId: siteId,
			pathway: 'chat',
		},
	);
};

/**
 * Wraps button in a div that plays the top-nav flare animation.
 */
const AnimatedFlareWrapper = ({
	productKey,
	children,
}: {
	productKey: string;
	children: React.ReactNode;
}) => {
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'viewed',
			actionSubject: 'animation',
			actionSubjectId: 'conversationAssistantButtonFlareAnimation',
			attributes: {
				productKey,
			},
		});
	}, [sendAnalyticsEvent, productKey]);

	return (
		<div
			data-testid="animated-flare-wrapper"
			css={[conversationAssistantButtonWrapperNoOverlayStyles, topNavButtonFlareAnimationStyles]}
		>
			{children}
		</div>
	);
};

type ButtonComponentProps = {
	onMouseEnter?: React.MouseEventHandler;
	style?: React.CSSProperties;
	iconBefore?: React.ReactChild;
	testId?: string;
	isSelected?: boolean;
	onClick?: PrimaryButtonProps['onClick'];
	isDisabled?: boolean;
	tooltip?: React.ReactNode;
	shortcut?: TooltipProps['shortcut'];
	children: React.ReactNode;
	interactionName?: string;
};

type HTMLElementForwardedRefComponent = React.ForwardRefExoticComponent<
	ButtonComponentProps & React.RefAttributes<HTMLElement>
>;
type HTMLButtonElementForwardedRefComponent = React.ForwardRefExoticComponent<
	ButtonComponentProps & React.RefAttributes<HTMLButtonElement>
>;

const getConversationAssistantButton = (
	Button: HTMLButtonElementForwardedRefComponent | HTMLElementForwardedRefComponent = PrimaryButton,
) =>
	forwardRef(
		(
			{
				testId,
				isActive,
				isDisabled = false,
				onClick: onClickProp,
				onMouseEnter: onMouseEnterProp,
				label,
				tooltipContent,
				shortcut,
				orgId,
				siteId,
				productKey,
				analyticsAttributes,
				enableOnboardingSpotlight = false,
			}: Props,
			ref: React.Ref<HTMLButtonElement>,
		) => {
			di(FeatureGates, useInsightsStatus);
			const { formatMessage } = useIntl();
			const { start } = useConversationAssistantSpotlightActions();
			const { PostOfficePlacement } = usePostOfficePlacement();
			// Read from context only: this must not trigger a profile request just by mounting.
			const profileAaid = useContext(ProfileContext)?.aaid;

			// Prefetch insights status to warm the cache before the user opens chat.
			// This triggers the backend to generate insights asynchronously if not already cached.
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			const isPulseExperimentEnabled = FeatureGates.getExperimentValue(
				'rovo_growth_chat_pulse',
				'isEnabled',
				false,
			);
			useInsightsStatus({
				enabled: isPulseExperimentEnabled,
				assistanceServiceParams: {
					touchpointSource: 'ask-rovo-button-prefetch',
					product: productKey,
					experienceId: 'ai-mate',
					siteId,
				},
			});
			const [prefersReducedMotion] = useState(
				() =>
					typeof window !== 'undefined' &&
					window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
			);
			const [shouldShowMessage, setShouldShowMessage] = useState(false);
			const messageId =
				productKey === 'confluence'
					? 'confluence_rovo_chat_agents_spotlight_ga'
					: productKey === 'jira'
						? 'jira_rovo_chat_agents_spotlight_ga'
						: '';
			const shouldUseCoordination = enableOnboardingSpotlight && !!messageId;
			const { sendAnalyticsEvent } = useAnalytics();

			// Capture initial prop values in a ref so the rendered event fires exactly once
			// on mount, even if reactive props like isActive/isDisabled change later.
			const renderedAttributesRef = useRef({ isActive, isDisabled, productKey });
			const sendAnalyticsEventRef = useRef(sendAnalyticsEvent);
			sendAnalyticsEventRef.current = sendAnalyticsEvent;
			useEffect(() => {
				sendAnalyticsEventRef.current({
					eventType: 'operational',
					action: 'rendered',
					actionSubject: 'rovoChatButton',
					attributes: renderedAttributesRef.current,
				});
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, []);

			const openRovoChat = (
				event: React.MouseEvent<HTMLElement, MouseEvent>,
				analyticsEvent: UIAnalyticsEvent,
			) => {
				if (!isActive && onClick) {
					onClick(event, analyticsEvent);
				}
			};

			const onClick = useCallback<NonNullable<typeof onClickProp>>(
				(...params) => {
					sendAnalyticsEvent({
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'rovoChatButton',
						attributes: {
							isActive,
							isDisabled,
							productKey,
							...analyticsAttributes,
						},
					});

					return onClickProp?.(...params);
				},
				[analyticsAttributes, isActive, isDisabled, onClickProp, productKey, sendAnalyticsEvent],
			);

			const interactionName = isActive ? 'close-rovo-chat-nav-button' : 'open-rovo-chat-nav-button';
			const buttonLabel = label ?? formatMessage(messages.rovoChatButtonLabel);
			const getChildren = () => {
				const button = (
					<Button
						data-spotlight-target="rovo-chat-ask-rovo-button"
						onMouseEnter={(event) => {
							onMouseEnterProp?.(event);
							// Fill the prompt-of-the-day cache before the panel opens, so it renders without a wait.
							void prefetchPromptOfTheDay(profileAaid);
							sendAnalyticsEvent({
								eventType: 'ui',
								action: 'hovered',
								actionSubject: 'rovoChatButton',
								attributes: {
									isActive,
									isDisabled,
									productKey,
								},
							});
							if (shouldShowMessage) {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'hovered',
									actionSubject: 'rovoChatButtonSpotlight',
								});
								start();
							}
						}}
						testId={testId}
						isSelected={isActive}
						onClick={onClick}
						isDisabled={isDisabled}
						tooltip={
							tooltipContent ?? <Fragment>{formatMessage(messages.rovoChatButtonLabel)}</Fragment>
						}
						shortcut={shortcut}
						style={{
							/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */
							...defaultButtonStyles,
							...(!isActive && inactiveButtonStyles),
							...(isDisabled && disabledButtonStyles),
							/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
						}}
						// The button's own visible/accessible label ("Ask Rovo") already conveys the
						// icon's meaning, so mark the icon as decorative to avoid a redundant
						// "Rovo Ask Rovo" screen reader announcement.
						iconBefore={<RovoIcon size="xxsmall" label="" />}
						interactionName={interactionName}
						ref={ref}
					>
						{buttonLabel}
					</Button>
				);

				if (Button === PrimaryButton) {
					return <div role="list">{button}</div>;
				}

				if (!isActive && !isDisabled) {
					const shouldAnimateTopNavButton =
						!prefersReducedMotion &&
						// Targets non-MAU Rovo Chat users — once a user becomes MAU, the gate
						// resolves to false on their next session (batch trait refreshes daily).
						fg('rovo_chat_non_mau_rovo_button_animation_filter') &&
						// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
						FeatureGates.getExperimentValue(
							'ask_rovo_top_nav_button_animation',
							'isEnabled',
							false,
						);

					if (shouldAnimateTopNavButton) {
						return <AnimatedFlareWrapper productKey={productKey}>{button}</AnimatedFlareWrapper>;
					}

					return <div css={conversationAssistantButtonWrapperNoOverlayStyles}>{button}</div>;
				}

				return button;
			};

			// The long-running-task PostOffice placement is anchored to the Ask Rovo
			// button (via data-spotlight-target) and must mount for every product
			// including Townsquare (productKey="atlas"), so it lives outside
			// SpotlightWrapper which only renders for products that opt into the
			// agents-onboarding coordination flow.
			// Uses <Fragment> (not <>) because this file has @jsx pragma without @jsxFrag.
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			const isFullPageSpotlightEnabled =
				productKey === 'jira' &&
				FeatureGates.getExperimentValue('rovo_chat_full_page_chat_spotlight', 'isEnabled', false);
			// FOMO spotlight for Atlas home (townsquare).
			//
			// This is a plain killswitch, NOT an experiment read. Randomization and
			// exposure for `rovo_chat_fomo_spotlight_in_home` happen in exactly one
			// place: inside the spotlight's own eligibility gate, after the non-MAU
			// and has-collaborators checks (see AtlasHomeFomoSpotlightConsumer).
			// Reading the experiment here as well would assign every Atlas user who
			// renders this button — including Rovo Chat MAU users and users with no
			// collaborators, who can never see the spotlight — blending the
			// value-proof and social-proof arms into one indistinguishable cell.
			//
			// `rovo_chat_fomo_spotlight_lifecycle` is the message template's own
			// lifecycle gate (see the Post Office template's config.ts and
			// filters/index.ts). Reusing it keeps one switch for "is this message
			// live": when it is off Post Office would not deliver anyway, so this
			// check just avoids mounting the placement and its fetch.
			const isFomoSpotlightEnabled =
				productKey === 'atlas' && fg('rovo_chat_fomo_spotlight_lifecycle');
			return (
				<Fragment>
					{shouldUseCoordination || isFullPageSpotlightEnabled || isFomoSpotlightEnabled ? (
						<SpotlightWrapper
							setShouldShowMessage={setShouldShowMessage}
							messageId={messageId}
							orgId={orgId}
							siteId={siteId}
							children={getChildren()}
							shouldShowMessage={shouldShowMessage}
							openRovoChat={(event, analyticsEvent?) => openRovoChat(event, analyticsEvent!)}
							isFullPageSpotlightEnabled={isFullPageSpotlightEnabled}
							isFomoSpotlightEnabled={isFomoSpotlightEnabled}
						/>
					) : (
						getChildren()
					)}
					{PostOfficePlacement && fg('rovo_chat_max_mode_spotlight') && (
						<PostOfficePlacement
							placementId="rovo-chat-long-running-task-spotlight"
							// This placement fetches its own data. Disable the outer request to avoid duplicates
							// when React replays renders during Suspense retries.
							disableInitialData
						/>
					)}
				</Fragment>
			);
		},
	);

const Nav4Button = forwardRef(
	(
		{ tooltip, shortcut, ...props }: ButtonComponentProps,
		forwardedRef: React.Ref<HTMLButtonElement>,
	) => {
		return (
			<Tooltip
				content={tooltip}
				shortcut={shortcut}
				// This button's visible text matches the tooltip
				// Announcing the tooltip means 'Chat' would be announced twice
				isScreenReaderAnnouncementDisabled
			>
				{({ ref: tooltipRef, ...triggerProps }) => (
					<TopNavButton
						interactionName={props?.interactionName}
						ref={mergeRefs([forwardedRef, tooltipRef])}
						{...triggerProps}
						{...props}
						onClick={(event, analyticsEvent) => {
							props.onClick?.(event, analyticsEvent);
							triggerProps.onClick?.(event);
						}}
						iconBefore={(() => props.iconBefore || null) as IconButtonProps['icon']}
					/>
				)}
			</Tooltip>
		);
	},
);

export const ConversationAssistantButton: ForwardRefExoticComponent<
	Props & RefAttributes<HTMLButtonElement>
> = getConversationAssistantButton(PrimaryButton);
export const ConversationAssistantButtonNav4: ForwardRefExoticComponent<
	Props & RefAttributes<HTMLButtonElement>
> = getConversationAssistantButton(Nav4Button);

export const SpotlightWrapper = ({
	children,
	messageId,
	orgId,
	siteId,
	setShouldShowMessage,
	shouldShowMessage,
	openRovoChat,
	isFullPageSpotlightEnabled = false,
	isFomoSpotlightEnabled = false,
}: {
	children: ConditionalWrapperChildren;
	messageId: string;
	orgId?: string;
	siteId: string;
	setShouldShowMessage: React.Dispatch<React.SetStateAction<boolean>>;
	shouldShowMessage: boolean;
	openRovoChat: (
		event: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent?: UIAnalyticsEvent,
	) => void;
	isFullPageSpotlightEnabled?: boolean;
	isFomoSpotlightEnabled?: boolean;
}): JSX.Element => {
	di(usePostOfficeCoordination);
	const { activeSpotlight } = useConversationAssistantSpotlightState();
	const [onboardingStarted, stopOnboarding] = usePostOfficeCoordination(messageId);
	const { sendAnalyticsEvent } = useAnalytics();

	const onDismissMessage = () => {
		setShouldShowMessage(false);
		stopOnboarding();
	};
	const openFullPageRovoChat = useCallback(() => {
		window.location.assign(getAtlasChatUrl(siteId, orgId));
	}, [orgId, siteId]);

	useEffect(() => {
		if (shouldShowMessage && activeSpotlight !== null) {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'shown',
				actionSubject: 'rovoChatButtonSpotlight',
			});
		}
	}, [shouldShowMessage, activeSpotlight, sendAnalyticsEvent]);

	useEffect(() => {
		if (onboardingStarted) {
			setShouldShowMessage(true);
		}
	}, [onboardingStarted, setShouldShowMessage]);
	// Mutually exclusive with the onboarding spotlight — don't show both at once
	const shouldShowFullPageSpotlight = isFullPageSpotlightEnabled && !shouldShowMessage;
	const shouldShowFomoSpotlight = isFomoSpotlightEnabled && !shouldShowMessage;

	return (
		<ConditionalWrapper
			condition={shouldShowMessage || shouldShowFullPageSpotlight || shouldShowFomoSpotlight}
			wrapper={(children) => (
				<PopoverProvider>
					<PopoverTarget>
						<div
							css={[
								shouldShowMessage && activeSpotlight === null && pulseAnimationStyles,
								reduceMotionStyles,
							]}
						>
							{children}
							{shouldShowFullPageSpotlight && (
								<ChunkErrorBoundary>
									<Suspense fallback={null}>
										<AsyncRovoFullPageSpotlightPlacement openRovoChat={openFullPageRovoChat} />
									</Suspense>
								</ChunkErrorBoundary>
							)}
							{shouldShowFomoSpotlight && (
								<ChunkErrorBoundary>
									<Suspense fallback={null}>
										<AsyncAtlasHomeFomoSpotlightPlacement />
									</Suspense>
								</ChunkErrorBoundary>
							)}
						</div>
					</PopoverTarget>
					<ConversationAssistantOnboardingSpotlight
						onDismiss={onDismissMessage}
						openRovoChat={openRovoChat}
					/>
				</PopoverProvider>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
};
